import { DoubleIndex, Logger } from '@deqo/foundation';
import { Component, ObjectManager } from '../../../concepts/objects';
import { ActiveObjectWithEvents, resolve } from '../../Container';
import { Concealable } from './Concealable';

const metrics = Logger.get('app.metrics');

export type ConcealmentIndexKey = readonly [string, string];

export class ConcealmentIndex extends ActiveObjectWithEvents<{ updated: Concealable[] }> {

    public static readonly noOwnerKey = 'NONE';

    @resolve(ObjectManager) private objects!: ObjectManager;

    private readonly forwardIndex = new DoubleIndex<string, Concealable>();
    private readonly reverseIndex = new Map<Concealable, ConcealmentIndexKey>();
    private readonly invalidations = new Set<Concealable>();

    public start(): void {
        const results = this.objects.getAll(Concealable);
        results.forEach(x => this.add(x));
        this.objects.on('updated', this.onUpdate);
    }

    public lookup(userId: string, concealed: boolean | '*'): ReadonlySet<Concealable> {
        this.refresh();
        return this.forwardIndex.get(userId, concealed?.toString());
    }

    public count(userId: string, concealed: boolean | '*') {
        return this.lookup(userId, concealed).size;
    }

    public invalidate(ce: Concealable) {
        this.invalidations.add(ce);
        this.refresh();
    }

    public invalidateAll() {
        for (const ce of this.reverseIndex.keys()) {
            this.invalidations.add(ce);
        }
        this.refresh();
    }

    public refresh() {
        const affectedKeys = new Set<ConcealmentIndexKey>();
        for (const ce of this.invalidations) {
            const old = this.remove(ce);
            if (old) affectedKeys.add(old);
            if (ce.alive) {
                const neu = this.add(ce);
                if (neu) affectedKeys.add(neu);
            }
        }
        const elmts = Array.from(this.invalidations);
        this.invalidations.clear();
        if (affectedKeys.size) {
            this.events.emit('updated', elmts);
        }
    }

    private add(ce: Concealable) {
        const userId = ce.userId || ConcealmentIndex.noOwnerKey;
        const key = [userId, ce.concealed?.toString()] as const;
        this.forwardIndex.add(key[0], key[1], ce);
        this.forwardIndex.add(key[0], '*', ce);
        this.reverseIndex.set(ce, key);
        return key;
    }

    private remove(ce: Concealable) {
        const key = this.reverseIndex.get(ce);
        if (key) {
            this.forwardIndex.remove(key[0], key[1], ce);
            this.forwardIndex.remove(key[0], '*', ce);
            this.reverseIndex.delete(ce);
            return key;
        }
        return null;
    }

    private onUpdate = (affected: Component[]) => {
        for (const c of affected) {
            if (c.is(Concealable)) {
                this.invalidations.add(c as any);
            }
        }
        this.refresh();
    };
}
